// ============================================================
// 广发圩 - App 入口（PC/H5 响应式适配）
// ============================================================

import { lazy, Suspense, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Spin, App as AntApp } from 'antd'
import { DeviceProvider, useDeviceContext } from './components/DeviceProvider'
import PCLayout from './layouts/PCLayout'
import H5Layout from './layouts/H5Layout'
import AdminLayout from './layouts/AdminLayout'
import AdminGuard from './components/AdminGuard'
import AuthGuard from './components/AuthGuard'
import SignInModal from './components/SignInModal'
import LoginModal from './components/LoginModal'
import ParticleBackground from './components/ParticleBackground'
import { useSignIn } from './hooks/useSignIn'
import { useAuthStore } from './stores/auth.store'
import { getLoginModalCallback, clearLoginModalCallback } from './components/LoginModal'

// ─── 样式导入 ──────────────────────────────────────────────

import './index.css'
import './styles/theme-dark.css'

// ─── 页面懒加载 ─────────────────────────────────────────────

const HomePage = lazy(() => import('./pages/Home'))
const ShopPage = lazy(() => import('./pages/Shop'))
const MyPage = lazy(() => import('./pages/My'))
const TaskDetailPage = lazy(() => import('./pages/TaskDetail'))
const LoginPage = lazy(() => import('./pages/Login'))

// 个人中心子页面
const MyProfilePage = lazy(() => import('./pages/MyProfile'))
const MyPublishedTasksPage = lazy(() => import('./pages/MyPublishedTasks'))
const MyAcceptedTasksPage = lazy(() => import('./pages/MyAcceptedTasks'))
const PointsDetailPage = lazy(() => import('./pages/PointsDetail'))
const BillingHistoryPage = lazy(() => import('./pages/BillingHistory'))
const PointsRechargePage = lazy(() => import('./pages/PointsRecharge'))
const PointsWithdrawPage = lazy(() => import('./pages/PointsWithdraw'))
const MembershipPage = lazy(() => import('./pages/Membership'))
const InvitePage = lazy(() => import('./pages/Invite'))
const NotificationsPage = lazy(() => import('./pages/Notifications'))
const NotificationDetailPage = lazy(() => import('./pages/NotificationDetail'))
const ExchangeOrdersPage = lazy(() => import('./pages/ExchangeOrders'))
const UserAgreementPage = lazy(() => import('./pages/UserAgreement'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy'))
const InviteLinkPage = lazy(() => import('./pages/InviteLink'))

// 后台管理页面
const AdminDashboardPage = lazy(() => import('./pages/admin/Dashboard'))
const AdminTaskManagementPage = lazy(() => import('./pages/admin/TaskManagement'))
const AdminUserManagementPage = lazy(() => import('./pages/admin/UserManagement'))
const AdminProductManagementPage = lazy(() => import('./pages/admin/ProductManagement'))
const AdminAnnouncementManagementPage = lazy(() => import('./pages/admin/AnnouncementManagement'))
const AdminPointsManagementPage = lazy(() => import('./pages/admin/PointsManagement'))
const AdminDepositManagementPage = lazy(() => import('./pages/admin/DepositManagement'))
const AdminWithdrawalManagementPage = lazy(() => import('./pages/admin/WithdrawalManagement'))
const AdminPaymentSettingsPage = lazy(() => import('./pages/admin/PaymentSettings'))
const AdminExchangeOrdersPage = lazy(() => import('./pages/admin/ExchangeOrdersManagement'))
const AdminArbitrationPage = lazy(() => import('./pages/admin/ArbitrationManagement'))
const AdminOperationLogPage = lazy(() => import('./pages/admin/OperationLog'))

// ─── Loading ────────────────────────────────────────────────

function PageLoading() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: '#8B5CF6',
      }}
    >
      <Spin size="large" />
    </div>
  )
}

// ─── 响应式布局壳（根据设备选择布局）+ 签到弹窗 + 登录弹窗 ──────

function ResponsiveLayoutWithSignIn() {
  const { isMobile } = useDeviceContext()
  const { isLoggedIn, loadProfile } = useAuthStore()
  const signInProps = useSignIn(isLoggedIn)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [loginModalInviteCode, setLoginModalInviteCode] = useState<string | undefined>()
  const [loginModalDefaultRegister, setLoginModalDefaultRegister] = useState(false)

  // 登录后每次路由变化刷新用户数据，确保余额等信息实时更新
  useEffect(() => {
    if (isLoggedIn) {
      loadProfile()
    }
  }, [isLoggedIn, window.location.pathname])

  // 监听登录弹窗打开事件
  useEffect(() => {
    const handleOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      setLoginModalInviteCode(detail.inviteCode)
      setLoginModalDefaultRegister(detail.defaultRegister || false)
      setLoginModalOpen(true)
    }
    window.addEventListener('login-modal:open', handleOpen)
    return () => window.removeEventListener('login-modal:open', handleOpen)
  }, [])

  const handleLoginSuccess = () => {
    const callback = getLoginModalCallback()
    if (callback) {
      callback()
      clearLoginModalCallback()
    }
  }

  return (
    <>
      {/* 粒子背景 */}
      <ParticleBackground />

      {/* 主布局 */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {isMobile ? <H5Layout /> : <PCLayout />}
      </div>

      {/* 签到弹窗 */}
      <SignInModal {...signInProps} />

      {/* 登录弹窗 */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => {
          setLoginModalOpen(false)
          setLoginModalInviteCode(undefined)
          setLoginModalDefaultRegister(false)
        }}
        onSuccess={handleLoginSuccess}
        initialInviteCode={loginModalInviteCode}
        defaultRegister={loginModalDefaultRegister}
      />
    </>
  )
}

// ─── App 根组件 ─────────────────────────────────────────────

export default function App() {
  return (
    <AntApp>
      <BrowserRouter>
        <DeviceProvider>
          <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* 所有页面共用响应式布局壳 + 签到弹窗 */}
            <Route element={<ResponsiveLayoutWithSignIn />}>
              <Route index element={<HomePage />} />
              <Route path="shop" element={<ShopPage />} />
              <Route path="tasks/:id" element={<TaskDetailPage />} />

              {/* 个人中心 - 需要登录 */}
              <Route path="my" element={<AuthGuard />}>
                <Route index element={<MyPage />} />
                <Route path="profile" element={<MyProfilePage />} />
                <Route path="published" element={<MyPublishedTasksPage />} />
                <Route path="accepted" element={<MyAcceptedTasksPage />} />
                <Route path="points" element={<PointsDetailPage />} />
                <Route path="billing" element={<BillingHistoryPage />} />
                <Route path="recharge" element={<PointsRechargePage />} />
                <Route path="withdraw" element={<PointsWithdrawPage />} />
                <Route path="membership" element={<MembershipPage />} />
                <Route path="invite" element={<InvitePage />} />
                <Route path="orders" element={<ExchangeOrdersPage />} />
              </Route>

              {/* 消息通知 - 需要登录 */}
              <Route path="notifications" element={<AuthGuard />}>
                <Route index element={<NotificationsPage />} />
                <Route path=":id" element={<NotificationDetailPage />} />
              </Route>

              {/* 用户协议和隐私政策 */}
              <Route path="agreement" element={<UserAgreementPage />} />
              <Route path="privacy" element={<PrivacyPolicyPage />} />

              {/* 邀请链接 */}
              <Route path="invite/:code" element={<InviteLinkPage />} />
            </Route>

            {/* 登录页独立布局（不含签到弹窗） */}
            <Route path="login" element={<LoginPage />} />

            {/* 后台管理 - 需要管理员权限 */}
            <Route path="admin" element={<AdminGuard />}>
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="tasks" element={<AdminTaskManagementPage />} />
                <Route path="users" element={<AdminUserManagementPage />} />
                <Route path="products" element={<AdminProductManagementPage />} />
                <Route path="announcements" element={<AdminAnnouncementManagementPage />} />
                <Route path="points" element={<AdminPointsManagementPage />} />
                <Route path="deposits" element={<AdminDepositManagementPage />} />
                <Route path="withdrawals" element={<AdminWithdrawalManagementPage />} />
                <Route path="exchange-orders" element={<AdminExchangeOrdersPage />} />
                <Route path="arbitrations" element={<AdminArbitrationPage />} />
                <Route path="payment-settings" element={<AdminPaymentSettingsPage />} />
                <Route path="logs" element={<AdminOperationLogPage />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </DeviceProvider>
    </BrowserRouter>
    </AntApp>
  )
}
